import { IOrganizationTeamMember } from '@copilot/common/pages/organizationDashboard/orgTeamMembers/data/models';
import {
	Button, Col, Divider, Input, Row, Select, Form, Space, theme,
} from 'antd';
import { FormProps } from 'antd/lib/form';
import {
	FC, ReactNode, useCallback, useMemo, useState,
} from 'react';
import Text from 'antd/lib/typography/Text';
import { ERROR_ICON, OK_ICON } from '../../icon';
import modalManager from '@copilot/common/utils/modalManager';
import { IProxy } from '@copilot/common/store/models/redux';
import styled from 'styled-components';
import * as _ from 'lodash';
import { arrayToMap } from '@copilot/common/utils';
import { BasicOrgMemberInfo } from '../../drawer/wrappers/adminMember/types';

const NO_VPN_SELECTED = 0;

const StyledForm = styled((props: FormProps<BasicOrgMemberInfo> & { children?: ReactNode }) => <Form {...props} />)`
${(props) => {
		const prefix = props.theme['@ant-prefix'];
		return `
		.${prefix}-form-item {
			margin-bottom: 0;
		}
		.${prefix}-form-item-label {
			width: 120px;
			text-align: left;
		}
		.${prefix}-form-item-control {
			margin-left: 20%;
			width: 300px;
		}
	}
	`;
	}}
`;

interface ISyncLinkedInProps {
	/**
	 * Organization member to sync linkedin account
	 */
	orgMember: IOrganizationTeamMember;
	/**
	 * whether the organization member is inactive or not
	 */
	isMemberInactive: boolean;
}

/**
 * [Presentational] Display linkedin sync status and button for linkedin sync
 */
const SyncLinkedIn: FC<ISyncLinkedInProps> = (props) => {
	const { orgMember, isMemberInactive } = props;
	return (
		<Row gutter={16}>
			<Col>
				{ERROR_ICON}
			</Col>
			<Col>
				<Button
					onClick={() => modalManager.openLinkedinModal({
						user: orgMember, style: { display: 'inline-flex' }, centered: true, width: 'auto',
					})}
					disabled={isMemberInactive}
					size="small"
					type="primary"
					ghost
				>
					Sync
				</Button>
			</Col>
		</Row>
	);
};

interface IMemberActivationProps {
	/**
	 * whether the member is active
	 */
	isActive: boolean;
	/**
	 * callback on activation
	 */
	onActivate: () => void;
	/**
	 * callback on deactivation
	 */
	onDeactivate: () => void;
}

/**
 * [Presentational] Display member activation status and button for activation/deactivation action
 */
const MemberActivation: FC<IMemberActivationProps> = (props) => {
	const { isActive, onActivate, onDeactivate } = props;
	return (
		isActive ? (
			<Row gutter={16}>
				<Col>
					<Text>Active</Text>
				</Col>
				<Col>
					<Button onClick={onDeactivate} danger type="primary" size="small">Deactivate User</Button>
				</Col>
			</Row>
		) : (
			<Row gutter={16}>
				<Col>
					<Text>Inactive</Text>
				</Col>
				<Col>
					<Button type="primary" style={{ background: '#008000', borderColor: '#008000' }} size="small" onClick={onActivate}>Reactivate User</Button>
				</Col>
			</Row>
		)
	);
};

interface OrgMemberBasicInfoEditorProps {
	/**
	 * orgMember to update basic info
	 */
	orgMember: IOrganizationTeamMember;
	/**
	 * whether info is editable
	 */
	 isEditable: boolean;
	/**
	 * list of proxy options
	 */
	proxyOptions: IProxy[];
	/**
	 * Callback on save
	 */
	onSave: (values: BasicOrgMemberInfo) => void;
	/**
	 * Callback on resent email
	 */
	onResendEmail: () => void;
	/**
	 * Callback on activate user
	 */
	onActivate: () => void;
	/**
	 * Callback on deactive user
	 */
	onDeactivate: () => void;
}

/**
 * [Presentational] Editors for orgMember basic info
 */
const OrgMemberBasicInfoEditor: FC<OrgMemberBasicInfoEditorProps> = (props) => {
	const {
		isEditable, onSave, orgMember, onResendEmail, proxyOptions, onActivate, onDeactivate,
	} = props;
	
	const { token } = theme.useToken();

	const [isEditing, setIsEditing] = useState<boolean>(false);
	const isActive = useMemo(() => orgMember.isActive, [orgMember.isActive]);
	const isLinkedInSynced = useMemo(() => orgMember.isLinkedInLoggedIn, [orgMember.isLinkedInLoggedIn]);

	const vpnOptions = useMemo(
		() =>
			proxyOptions.map((proxy) => (
				<Select.Option key={proxy.value} value={proxy.value}>
					{proxy.name}
				</Select.Option>
			)),
		[proxyOptions]
	);

	const vpnPortNames = useMemo(() => arrayToMap(proxyOptions, (option) => option.value), [proxyOptions]);
	const selectedVpnPortName = useMemo(() => vpnPortNames.get(orgMember.vpnPort)?.name ?? 'Not Selected', [vpnPortNames, orgMember.vpnPort]);

	const [form] = Form.useForm<BasicOrgMemberInfo>();

	const handleFinish = useCallback((values: BasicOrgMemberInfo) => {
		onSave(values);
		setIsEditing(false);
	}, [onSave]);

	const handleCancel = useCallback(() => {
		form.resetFields();
		setIsEditing(false);
	}, [form]);

	return (
		<Space direction={'vertical'} style={{ width: '100%' }}>
			<Row align={'middle'} gutter={token.size} justify={'space-between'}>
				<Col>
					<Space>
						<Text style={{ fontSize: token.fontSizeHeading3 }} strong>
							Details
						</Text>
						{isEditable && !isEditing && (
							<Button size="small" style={{ marginLeft: '4px' }} onClick={() => setIsEditing(true)}>
								Edit
							</Button>
						)}
					</Space>
				</Col>
				<Col>
					<Button onClick={onResendEmail} disabled={!isActive} size="small" style={{ float: 'right' }}>Resend Login E-mail</Button>
				</Col>
			</Row>

			<Divider style={{ margin: 0 }} />
			<Row>
				<Col span={22}>
					<StyledForm
						form={form}
						initialValues={{
							firstName: orgMember.firstName,
							lastName: orgMember.lastName,
							email: orgMember.email,
							vpnPort: orgMember.vpnPort === NO_VPN_SELECTED ? undefined : orgMember.vpnPort,
						}}
						onFinish={handleFinish}
					>
						<Form.Item
							label="Member Status"
						>
							<MemberActivation isActive={isActive} onActivate={onActivate} onDeactivate={onDeactivate} />
						</Form.Item>
						<Form.Item
							name="firstName"
							label="First Name"
						>
							{isEditing ?
								<Input size="small" /> :
								<Text>{orgMember.firstName}</Text>}
						</Form.Item>
						<Form.Item
							name="lastName"
							label="Last Name"
						>
							{isEditing ?
								<Input size="small" /> :
								<Text>{orgMember.lastName}</Text>}
						</Form.Item>
						<Form.Item
							name="email"
							label="E-mail"
						>
							<Text>{orgMember.email}</Text>
						</Form.Item>
						<Form.Item
							label="LinkedIn Status"
						>
							{isLinkedInSynced ? OK_ICON : <SyncLinkedIn orgMember={orgMember} isMemberInactive={!isActive} />}
						</Form.Item>
						<Form.Item
							name="vpnPort"
							label="VPN Port"
						>
							{isEditing ? (
								<Select
									size="small"
									placeholder="Not Selected"
								>
									{vpnOptions}
								</Select>
							) : <Text>{selectedVpnPortName}</Text>}
						</Form.Item>
						{isEditing && (
						<Form.Item>
							<Row justify="end" gutter={16} style={{ marginTop: '10px' }}>
								<Col>
									<Button size="small" onClick={handleCancel}>Cancel</Button>
								</Col>
								<Col>
									<Button size="small" type="primary" htmlType="submit">Save</Button>
								</Col>
							</Row>
						</Form.Item>
						)}
					</StyledForm>

				</Col>
			</Row>
		</Space>
	);
};

export default OrgMemberBasicInfoEditor;
